import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { BrowserStorage } from '@estee/elc-universal-utils';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/VtoService';
import { VTO_MATCHES, VTO, VTO_PERSONALIZATION_VERIFIED_COOKIE } from '~constants/Vto';
import { Personalization } from '~domain/entities/Personalization';
import { IVtoMatchCookie } from '~interfaces/IVto';
import { PersonalizationRepository } from '~repositories/PersonalizationRepository';
import { UserInfoRepository } from '~repositories/UserInfoRepository';
import { setVtoMatchCookie } from '~utils/setVtoMatchCookie';
import { getVtoMatchCookie } from '~utils/getVtoMatchCookie';
import { IConfig } from '~interfaces/IConfig';

interface IBestMatchUseCase {
    productId: string;
}

const { name: serviceName, version: serviceVersion } = __serviceInfo__;

export class BestMatchUseCase {
    private personalizationRepository: PersonalizationRepository;
    private userInfoRepository: UserInfoRepository;
    private productId: string;
    private logger: ELCLogger;
    private configStore: IConfig;

    constructor(payload: IBestMatchUseCase) {
        this.personalizationRepository = diContainer.get(serviceNames.personalizationRepository);
        this.userInfoRepository = diContainer.get(serviceNames.userInfoRepository);
        this.configStore = diContainer.get(serviceNames.configStore);
        this.logger = new ELCLogger({ serviceName, serviceVersion });
        this.productId = payload.productId;
    }

    public execute = async () => {
        if (!this.isSaveMatchEnabled) {
            return null;
        }

        const isPersonalizationVerified = this.getPersonalizationVerifiedCookie();

        if (!isPersonalizationVerified) {
            await this.storePersonalizedDataToCookie();
        }

        const cookie = getVtoMatchCookie();

        return cookie && this.getBestMatchSkuId(cookie);
    };

    private get isSaveMatchEnabled() {
        const vtoFeature = this.configStore.config?.vtoConfig?.vtoFeature;

        return vtoFeature?.foundation?.isSaveMatchEnabled;
    }

    private async storePersonalizedDataToCookie() {
        const userMatches = await this.fetchPersonalizedData();
        if (userMatches && Object.keys(userMatches).length > 0) {
            setVtoMatchCookie({ shadeMatches: userMatches, shouldOverride: false });
        }
    }

    private getBestMatchSkuId(shadeMatchCookie: string) {
        let shadeMatches: IVtoMatchCookie = {};

        try {
            shadeMatches = JSON.parse(shadeMatchCookie);
        } catch (e) {
            console.warn(e);
        }

        const shadeMatch = shadeMatches[this.productId];

        return shadeMatch && shadeMatch.userSelectedMatch;
    }

    private async fetchPersonalizedData() {
        let userId;
        try {
            userId = await this.userInfoRepository.getUserId();
            if (!userId) {
                return;
            }

            const data = await this.personalizationRepository.getPersonalizationDataByPerlgemUserId(
                [VTO_MATCHES],
                userId
            );

            this.setPersonalizationVerifiedCookie();

            if (Array.isArray(data)) {
                return this.getPersonalizedData(data);
            }
        } catch (e) {
            userId && this.setPersonalizationVerifiedCookie();

            this.logger.warning({
                triggerType: TriggerType.api,
                message: e.message,
                payload: { error: e }
            });
        }
    }

    private getPersonalizedData(data: Personalization[]) {
        const personalizedData = data
            .filter((match) => match.tags.includes(VTO))

            .sort(
                (a, b) => new Date(a.creationDate).valueOf() - new Date(b.creationDate).valueOf()
            );

        return this.getProcessedPersonalizedData(personalizedData);
    }

    private getProcessedPersonalizedData(data: Personalization[]) {
        try {
            return data.reduce((acc: IVtoMatchCookie, match) => {
                const userMatches = JSON.parse(match.userPersonalizationData);

                // backend wraps matches into zeroth array
                const personalizedData = userMatches?.[VTO_MATCHES]?.[0];

                if (personalizedData && personalizedData.userSelectedMatch) {
                    const { productId, userSelectedMatch, recommendations } = personalizedData;

                    acc[productId] = {
                        userSelectedMatch,
                        recommendations,
                        productId
                    };
                }

                return acc;
            }, {});
        } catch (e) {
            this.logger.error({
                triggerType: TriggerType.runtimeError,
                message: `${e}`
            });
        }
    }

    private setPersonalizationVerifiedCookie() {
        BrowserStorage.setItem(VTO_PERSONALIZATION_VERIFIED_COOKIE, '1');
    }

    private getPersonalizationVerifiedCookie() {
        return BrowserStorage.getItem(VTO_PERSONALIZATION_VERIFIED_COOKIE);
    }
}
